import useTranslation from "@/hooks/useTranslation";
import { ReactNode, useState, useRef, useEffect } from "react";
import { countries } from "@/utils/countryData";
import { CountryFlag } from "@/components/form/CountryFlag";

type PhoneInputProps = {
  id?: string;
  value?: PhoneInputValue;
  placeholder?: string;
  error?: boolean;
  name: string;
  label?: string;
  sizing?: "sm" | "md" | "lg";
  disabled?: boolean;
  readOnly?: boolean;
  color?: string;
  className?: string;
  helperText?: ReactNode;
  required?: boolean;
  onChange?: (value: PhoneInputValue) => void;
};

export type PhoneInputValue = {
  countryCodeValue: string;
  phoneValue: string;
  countryCodeISO2Value: string;
};

const PhoneInput: React.FC<PhoneInputProps> = ({
  id,
  value = {
    countryCodeValue: "+41",
    phoneValue: "",
    countryCodeISO2Value: "CH",
  },
  placeholder = "e.g. +1234567890",
  error,
  name,
  label,
  sizing = "md",
  disabled = false,
  readOnly = false,
  color = "",
  helperText,
  className = "",
  required = false,
  onChange = () => {},
}) => {
  const [isCountryCodeToggled, setIsCountryCodeToggled] = useState(false);
  const [filterText, setFilterText] = useState("");
  const { t } = useTranslation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const filterInputRef = useRef<HTMLInputElement>(null);

  // Handle clicks outside of the dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isCountryCodeToggled &&
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsCountryCodeToggled(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCountryCodeToggled]);

  // Focus filter input when dropdown is opened
  useEffect(() => {
    if (isCountryCodeToggled && filterInputRef.current) {
      filterInputRef.current.focus();
    }
  }, [isCountryCodeToggled]);

  const handleCountryCodeSelect = (
    countryCode: string,
    countryISO2: string,
  ) => {
    onChange({
      countryCodeValue: countryCode,
      phoneValue: value.phoneValue,
      countryCodeISO2Value: countryISO2,
    });
    setIsCountryCodeToggled(false);
  };

  // Filter countries based on search text
  const filteredCountries = countries.filter((country) => {
    const searchText = filterText.toLowerCase();
    return (
      country.name.toLowerCase().includes(searchText) ||
      country.code.toLowerCase().includes(searchText)
    );
  });

  return (
    <div className="flex items-center">
      <button
        id="dropdown-phone-button"
        data-dropdown-toggle="country-code-dropdown"
        className={`shrink-0 z-10 inline-flex items-center py-3 px-4 text-${sizing} font-medium text-center text-gray-900 bg-gray-50 border border-gray-300 rounded-s-lg hover:bg-gray-200 focus:ring-4 focus:outline-none focus:ring-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 dark:focus:ring-gray-700 dark:text-white dark:border-gray-600`}
        type="button"
        onClick={() => setIsCountryCodeToggled(!isCountryCodeToggled)}
        ref={buttonRef}
      >
        <CountryFlag
          countryCode={value.countryCodeISO2Value}
          svg
          style={{
            width: "1.5em",
            height: "1.5em",
            marginRight: "0.5em",
          }}
          title={value.countryCodeValue}
        />
        {value.countryCodeValue}{" "}
        <svg
          className="w-2.5 h-2.5 ms-2.5"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 10 6"
        >
          <path
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="m1 1 4 4 4-4"
          />
        </svg>
      </button>
      <div
        id="country-code-dropdown"
        className={`${isCountryCodeToggled ? "absolute" : "hidden"} z-10 bg-white divide-y divide-gray-100 rounded-lg shadow-sm w-52 dark:bg-gray-700`}
        ref={dropdownRef}
      >
        <div className="p-1">
          <input
            type="text"
            className="w-full p-2 border border-gray-300 rounded text-gray-900 text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white dark:placeholder-gray-400"
            placeholder={t["Filter..."]}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            ref={filterInputRef}
          />
        </div>
        <ul
          className="py-2 text-sm text-gray-700 dark:text-gray-200 max-h-[200px] overflow-y-auto"
          aria-labelledby="dropdown-phone-button"
        >
          {filteredCountries.length === 0 ? (
            <li className="px-4 py-2 text-gray-500 dark:text-gray-400">
              No matches found
            </li>
          ) : (
            filteredCountries.map((country) => (
              <li key={country.iso2}>
                <button
                  type="button"
                  className="inline-flex w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600 dark:hover:text-white"
                  role="menuitem"
                  onClick={() =>
                    handleCountryCodeSelect(country.code, country.iso2)
                  }
                >
                  <span className="inline-flex items-center">
                    <CountryFlag
                      countryCode={country.iso2}
                      svg
                      style={{
                        width: "1.5em",
                        height: "1.5em",
                        marginRight: "0.5em",
                      }}
                      title={country.name}
                    />
                    {country.name} ({country.code})
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
      <label className="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white">
        {t["Phone Number"]}
      </label>
      <div className="relative w-full">
        <input
          type="text"
          id="phone-input"
          className={`block p-3 w-full z-20 text-${sizing} text-gray-900 bg-gray-50 rounded-e-lg border-s-0 border border-gray-300 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-s-gray-700  dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:border-blue-500`}
          placeholder="1234567890"
          inputMode="tel"
          value={value.phoneValue}
          onChange={(e) => {
            // Only allow digits (0-9)
            const numericValue = e.target.value.replace(/\D/g, "");
            // Remove leading zeros
            const validValue = numericValue.replace(/^0+/, "");
            onChange &&
              onChange({
                countryCodeValue: value.countryCodeValue,
                phoneValue: validValue,
                countryCodeISO2Value: value.countryCodeISO2Value,
              });
          }}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
        />
      </div>
    </div>
  );
};

export default PhoneInput;
